/**
 * HealthService — runtime health checks for critical dependencies.
 *
 * Checks Supabase connectivity, auth session, repository
 * availability, environment configuration, email system,
 * and storage system. Returns a composite health status.
 */

import { getSupabase } from '@/lib/supabase';
import { env } from '@/lib/env';
import { LoggerFactory } from '@/lib/logging';
import { emailService } from '@/lib/email/EmailService';
import { emailQueue } from '@/lib/email/EmailQueue';
import { getRegisteredTemplates } from '@/lib/email/renderer';
import { storageService } from '@/lib/storage/StorageService';

export type HealthStatus = 'healthy' | 'warning' | 'critical';

export interface HealthCheckResult {
  name: string;
  status: HealthStatus;
  latencyMs?: number;
  message: string;
}

export interface HealthReport {
  overall: HealthStatus;
  checks: HealthCheckResult[];
  timestamp: string;
}

class HealthServiceImpl {
  private logger = LoggerFactory.create();

  async checkAll(): Promise<HealthReport> {
    const checks = await Promise.all([
      this.checkEnvironment(),
      this.checkSupabase(),
      this.checkAuth(),
      this.checkEmailProvider(),
      this.checkEmailQueue(),
      this.checkEmailTemplates(),
      this.checkStorageProvider(),
    ]);

    const overall = this.computeOverall(checks);
    return {
      overall,
      checks,
      timestamp: new Date().toISOString(),
    };
  }

  private checkEnvironment(): HealthCheckResult {
    const missing = env.getMissing();
    if (missing.length === 0) {
      return { name: 'environment', status: 'healthy', message: 'All required variables present.' };
    }
    return {
      name: 'environment',
      status: 'critical',
      message: `Missing: ${missing.join(', ')}`,
    };
  }

  private async checkSupabase(): Promise<HealthCheckResult> {
    const start = performance.now();
    try {
      const supabase = getSupabase();
      const { error } = await supabase.from('users').select('id').limit(1).maybeSingle();
      const latencyMs = Math.round(performance.now() - start);
      if (error) {
        return { name: 'supabase', status: 'critical', latencyMs, message: error.message };
      }
      return { name: 'supabase', status: 'healthy', latencyMs, message: 'Connected.' };
    } catch (err) {
      return {
        name: 'supabase',
        status: 'critical',
        message: err instanceof Error ? err.message : 'Connection failed.',
      };
    }
  }

  private async checkAuth(): Promise<HealthCheckResult> {
    try {
      const supabase = getSupabase();
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        return { name: 'auth', status: 'healthy', message: 'Active session.' };
      }
      return { name: 'auth', status: 'healthy', message: 'No active session (anonymous).' };
    } catch (err) {
      return {
        name: 'auth',
        status: 'warning',
        message: err instanceof Error ? err.message : 'Auth check failed.',
      };
    }
  }

  private computeOverall(checks: HealthCheckResult[]): HealthStatus {
    if (checks.some((c) => c.status === 'critical')) return 'critical';
    if (checks.some((c) => c.status === 'warning')) return 'warning';
    return 'healthy';
  }

  private async checkEmailProvider(): Promise<HealthCheckResult> {
    try {
      const provider = emailService.getProvider();
      const available = await provider.isAvailable();
      if (available) {
        return { name: 'email_provider', status: 'healthy', message: `Provider "${provider.name}" available.` };
      }
      return { name: 'email_provider', status: 'warning', message: `Provider "${provider.name}" not available.` };
    } catch (err) {
      return {
        name: 'email_provider',
        status: 'critical',
        message: err instanceof Error ? err.message : 'Email provider check failed.',
      };
    }
  }

  private checkEmailQueue(): HealthCheckResult {
    const stats = emailQueue.getQueueStats();
    if (stats.failed > 10) {
      return { name: 'email_queue', status: 'critical', message: `${stats.failed} failed emails in queue.` };
    }
    if (stats.failed > 0) {
      return { name: 'email_queue', status: 'warning', message: `${stats.failed} failed emails.` };
    }
    return { name: 'email_queue', status: 'healthy', message: `Queue: ${stats.queued} queued, ${stats.sent} sent.` };
  }

  private checkEmailTemplates(): HealthCheckResult {
    const templates = getRegisteredTemplates();
    if (templates.length === 0) {
      return { name: 'email_templates', status: 'critical', message: 'No templates registered.' };
    }
    return { name: 'email_templates', status: 'healthy', message: `${templates.length} templates available.` };
  }

  private async checkStorageProvider(): Promise<HealthCheckResult> {
    try {
      const provider = storageService.getProvider();
      const available = await provider.isAvailable();
      if (available) {
        return { name: 'storage_provider', status: 'healthy', message: `Provider "${provider.name}" available.` };
      }
      return { name: 'storage_provider', status: 'warning', message: `Provider "${provider.name}" not available.` };
    } catch (err) {
      return {
        name: 'storage_provider',
        status: 'critical',
        message: err instanceof Error ? err.message : 'Storage provider check failed.',
      };
    }
  }
}

export const healthService = new HealthServiceImpl();
